import { useTranslation } from "react-i18next";
import type { ThemeClasses, Brand } from "../types";
import Modal from "../../../../../../components/base/Modal";

interface ModelDetailsModalProps {
  brand: Brand;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onClose: () => void;
  onEdit: (brand: Brand) => void;
}

export function ModelDetailsModal({
  brand,
  themeClasses,
  isDarkMode,
  onClose,
  onEdit,
}: ModelDetailsModalProps) {
  const { t } = useTranslation("resellerAdmin");

  const InfoRow = ({ label, value }: { label: string; value: string | null | undefined }) => (
    <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0">
      <span className={`text-sm ${themeClasses.textSecondary}`}>{label}</span>
      <span className={`text-sm font-medium ${themeClasses.text}`}>
        {value || t("models.noData")}
      </span>
    </div>
  );

  return (
    <Modal
      width="w-full max-w-lg"
      content={
        <div className="space-y-6">
          {/* Header with icon */}
          <div className="flex items-center space-x-4 pb-4 border-b border-gray-200 dark:border-gray-700">
            <div
              className={`w-16 h-16 ${isDarkMode ? "bg-blue-900/30" : "bg-blue-100"} text-blue-500 rounded-xl flex items-center justify-center`}
            >
              <i className="ri-cpu-line text-3xl"></i>
            </div>
            <div>
              <h3 className={`text-xl font-bold ${themeClasses.text}`}>
                {brand.brand}
              </h3>
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {brand.model}
              </p>
            </div>
          </div>

          {/* Basic Info Section */}
          <div>
            <h4 className={`text-sm font-semibold ${themeClasses.text} mb-3`}>
              {t("models.sections.basicInfo")}
            </h4>
            <div className={`${isDarkMode ? "bg-dark-card" : "bg-gray-50"} rounded-lg p-4`}>
              <InfoRow label={t("models.fields.brand")} value={brand.brand} />
              <InfoRow label={t("models.fields.model")} value={brand.model} />
              <InfoRow label={t("models.fields.operationalAlias")} value={brand.operational_alias} />
              <InfoRow label={t("models.fields.origin")} value={brand.origin?.toUpperCase()} />
            </div>
          </div>

          {/* Network Info Section */}
          <div>
            <h4 className={`text-sm font-semibold ${themeClasses.text} mb-3`}>
              {t("models.sections.networkInfo")}
            </h4>
            <div className={`${isDarkMode ? "bg-dark-card" : "bg-gray-50"} rounded-lg p-4`}>
              <InfoRow label={t("models.fields.ipAddress")} value={brand.network?.ip_address} />
              <InfoRow label={t("models.fields.port")} value={brand.network?.port} />
              <InfoRow label={t("models.fields.protocol")} value={brand.network?.protocol} />
            </div>
          </div>

          {/* Commands indicator */}
          {brand.collections?.includes("commands") && (
            <div className={`${isDarkMode ? "bg-purple-900/20" : "bg-purple-50"} rounded-lg p-4 flex items-center space-x-3`}>
              <i className={`ri-terminal-box-line text-xl ${isDarkMode ? "text-purple-400" : "text-purple-600"}`}></i>
              <span className={`text-sm ${isDarkMode ? "text-purple-400" : "text-purple-700"}`}>
                {t("models.hasCommandsDescription")}
              </span>
            </div>
          )}
        </div>
      }
      themeClasses={themeClasses}
      onClose={onClose}
      title={t("models.modal.detailsTitle")}
      buttonAction={
        <button
          onClick={() => onEdit(brand)}
          className="px-4 py-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors"
        >
          <i className="ri-edit-line mr-2"></i>
          {t("models.actions.edit")}
        </button>
      }
    />
  );
}
